import { AvailabilityHint } from '@/components/leads/AvailabilityHint';
import {
  InteractionResult,
  InteractionType,
  RESULT_REQUIRED_TYPES,
  isLeadNotice,
  type AdvisorOption,
  type AppointmentInfo,
  type CatalogsResponse,
  type InteractionItem,
  type LeadActionNotice,
  type MyDayTask,
} from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { defaultNextTask, fmt, fromLocalInput, toLocalInput, TYPE_LABEL } from '@/lib/format';
import {
  useAnnulVisit,
  useAssignLead,
  useCancelInteraction,
  useCatalogs,
  useCompleteInteraction,
  useCreateInteraction,
  useDesistLead,
  useLead,
  useReopenLead,
  useRescheduleInteraction,
  useConfirmAppointment,
} from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { emptyAppointment, toAppointmentDetails, type AppointmentForm } from './appointment';
import { AppointmentFields } from './AppointmentFields';
import { NextStepFields, NextStepSummary } from './NextStepFields';
import {
  effectiveAppointmentAdvisor,
  emptyNextStep,
  toNextStep,
  toNextTask,
  type NextStepForm,
} from './nextStep';
import { occurredAtNow, occurredAtToDate } from './occurredAt';
import { OccurredAtField } from './OccurredAtField';

type Catalogs = Pick<CatalogsResponse, 'desistReasons' | 'nonEffectiveReasons'>;
const EMPTY: Catalogs = { desistReasons: [], nonEffectiveReasons: [] };

/** Aviso «el lead salió de tu alcance» de una respuesta de acción (ficha, o `{ id, lead }` al registrar). */
function noticeOf(r: unknown): LeadActionNotice | null {
  if (!r || typeof r !== 'object') return null;
  const inner = 'lead' in r ? (r as { lead: unknown }).lead : r;
  if (!inner || typeof inner !== 'object') return null;
  return isLeadNotice(inner as LeadActionNotice) ? (inner as LeadActionNotice) : null;
}

function useSubmit(onDone: () => void) {
  const toast = useToast();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  /** `successMsg` puede depender de la respuesta (p. ej. si la cita quedó concretada o solo marcada). */
  const run = async (
    fn: () => Promise<unknown>,
    successMsg: string | ((result: unknown) => string),
  ) => {
    setError(null);
    try {
      const result = await fn();
      const notice = noticeOf(result);
      onDone();
      if (notice) {
        // La acción se guardó pero el lead ya no es de quien la hizo (pasó al asesor de sala): a Mi día.
        toast.success(notice.message);
        navigate('/');
        return;
      }
      toast.success(typeof successMsg === 'function' ? successMsg(result) : successMsg);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'No se pudo guardar');
    }
  };
  return { error, run };
}

/** Dos opciones excluyentes sin valor por defecto (asistió / no asistió, se realizó / no se realizó). */
function OutcomeChoice({
  value,
  onChange,
  yes,
  no,
  testId,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  yes: string;
  no: string;
  testId: string;
}) {
  return (
    <div className="flex gap-2" role="group" aria-label="Resultado">
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={value === true}
        data-testid={`${testId}-yes`}
        className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${value === true ? 'border-emerald-600 bg-emerald-600 text-white' : 'bg-card'}`}
      >
        {yes}
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={value === false}
        data-testid={`${testId}-no`}
        className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${value === false ? 'border-amber-600 bg-amber-600 text-white' : 'bg-card'}`}
      >
        {no}
      </button>
    </div>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
      {error}
    </p>
  ) : null;
}

function ResultFields({
  result,
  setResult,
  reason,
  setReason,
  reasons,
}: {
  result: InteractionResult | '';
  setResult: (r: InteractionResult | '') => void;
  reason: string;
  setReason: (r: string) => void;
  reasons: Catalogs['nonEffectiveReasons'];
}) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setResult(InteractionResult.EFECTIVO)}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${result === 'EFECTIVO' ? 'border-emerald-600 bg-emerald-600 text-white' : 'bg-card'}`}
        >
          Contacto efectivo
        </button>
        <button
          type="button"
          onClick={() => setResult(InteractionResult.NO_EFECTIVO)}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${result === 'NO_EFECTIVO' ? 'border-amber-600 bg-amber-600 text-white' : 'bg-card'}`}
        >
          No efectivo
        </button>
      </div>
      {result === 'NO_EFECTIVO' && (
        <div className="space-y-1">
          <Label htmlFor="ne-reason">Motivo</Label>
          <Select
            id="ne-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          >
            <option value="">Selecciona…</option>
            {reasons.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- completar tarea

export function CompleteTaskDialog({
  task,
  leadId,
  companyId,
  leadActive = true,
  open,
  onOpenChange,
}: {
  task: MyDayTask | InteractionItem | null;
  leadId: string;
  companyId: string;
  leadActive?: boolean;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const cat = useCatalogs(companyId).data ?? EMPTY;
  const tz = useCompanyTimezone(companyId);
  const { data: lead } = useLead(leadId);
  const mutation = useCompleteInteraction(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [result, setResult] = useState<InteractionResult | ''>('');
  const [neReason, setNeReason] = useState('');
  const [description, setDescription] = useState('');
  // Visita: «se realizó» / «no se realizó» sin valor por defecto (es un hecho del embudo). Una cita no se completa
  // aquí: usa «Confirmar asistencia» (ConfirmAppointmentDialog).
  const [visited, setVisited] = useState<boolean | null>(null);
  const [occurredAt, setOccurredAt] = useState(occurredAtNow());
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));

  useEffect(() => {
    if (open) {
      setResult('');
      setNeReason('');
      setDescription('');
      setVisited(null);
      setOccurredAt(occurredAtNow());
      setNext(emptyNextStep(tz));
    }
  }, [open, task?.id, tz]);

  // Una cita nunca llega aquí (la API la concretaría o la cerraría sin asistencia según `visitAt`): se confirma
  // con ConfirmAppointmentDialog.
  if (!task || task.type === 'SE_CITO') return null;
  const needsResult = RESULT_REQUIRED_TYPES.includes(task.type);
  const isVisit = task.type === 'VISITA';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    // La gestión (y la visita, si la hubo) se imputan a la fecha en que ocurrieron, no a la de registro.
    const when = occurredAtToDate(occurredAt);
    void run(
      () =>
        mutation.mutateAsync({
          id: task.id,
          input: {
            result: needsResult ? (result as InteractionResult) : null,
            nonEffectiveReason: result === 'NO_EFECTIVO' ? neReason : null,
            description: description || null,
            completedAt: when,
            // Solo una visita lleva fecha de visita (se realizó / no se realizó); en las demás no se envía.
            visitAt: isVisit ? (visited ? when : null) : undefined,
            metadata: undefined,
            next: leadActive ? toNextStep(next, tz) : undefined,
          },
        }),
      'Tarea completada',
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Completar {TYPE_LABEL[task.type].toLowerCase()}</DialogTitle>
            <DialogDescription>
              {task.description ?? 'Registra el resultado y deja programado el siguiente paso.'}
            </DialogDescription>
          </DialogHeader>
          {needsResult && (
            <ResultFields
              result={result}
              setResult={setResult}
              reason={neReason}
              setReason={setNeReason}
              reasons={cat.nonEffectiveReasons}
            />
          )}
          {isVisit && (
            <div className="space-y-1">
              <p className="text-sm font-medium">¿La visita se realizó?</p>
              <OutcomeChoice
                value={visited}
                onChange={setVisited}
                yes="Sí, se realizó"
                no="No se realizó"
                testId="visit-outcome"
              />
              <p className="text-muted-foreground text-xs">
                Solo una visita realizada cuenta en el embudo y sube el lead a Visita.
              </p>
            </div>
          )}
          <OccurredAtField
            id="complete-occurred-at"
            value={occurredAt}
            onChange={setOccurredAt}
            label={isVisit && visited ? '¿Cuándo fue la visita?' : '¿Cuándo ocurrió?'}
          />
          <div className="space-y-1">
            <Label htmlFor="complete-desc">Comentario</Label>
            <Textarea
              id="complete-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="¿Qué se conversó? ¿Qué quedó pendiente?"
            />
          </div>
          {leadActive && (
            <NextStepFields
              availability={
                lead
                  ? { companyId, projectId: lead.projectId, advisorId: lead.advisorId }
                  : undefined
              }
              value={next}
              onChange={setNext}
              desistReasons={cat.desistReasons}
              projectId={lead?.projectId}
              tz={tz}
            />
          )}
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending || (needsResult && !result) || (isVisit && visited === null)
              }
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar
            </Button>
            {/* Último hijo: en móvil (columna invertida) queda encima de los botones; en escritorio, a la izquierda. */}
            {leadActive && (
              <NextStepSummary value={next} desistReasons={cat.desistReasons} tz={tz} />
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- registrar gestión / programar tarea

const LOG_TYPES: InteractionType[] = ['LLAMADA', 'WHATSAPP', 'MAIL', 'REUNION', 'VISITA', 'NOTA'];
const SCHEDULE_TYPES: InteractionType[] = [
  'LLAMADA',
  'WHATSAPP',
  'SE_CITO',
  'VISITA',
  'MAIL',
  'REUNION',
];

export function InteractionDialog({
  leadId,
  mode,
  companyId,
  leadActive = true,
  open,
  onOpenChange,
  trigger,
}: {
  leadId: string;
  mode: 'log' | 'schedule';
  companyId: string;
  leadActive?: boolean;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  trigger?: ReactNode;
}) {
  const cat = useCatalogs(companyId).data ?? EMPTY;
  const tz = useCompanyTimezone(companyId);
  const mutation = useCreateInteraction(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [type, setType] = useState<InteractionType>(
    mode === 'log' ? InteractionType.LLAMADA : InteractionType.LLAMADA,
  );
  const [result, setResult] = useState<InteractionResult | ''>('');
  const [neReason, setNeReason] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState(defaultNextTask());
  const [occurredAt, setOccurredAt] = useState(occurredAtNow());
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [appointment, setAppointment] = useState<AppointmentForm>(emptyAppointment());
  const { data: lead } = useLead(leadId);

  useEffect(() => {
    if (open) {
      setType(InteractionType.LLAMADA);
      setResult('');
      setNeReason('');
      setDescription('');
      setScheduledAt(defaultNextTask());
      setOccurredAt(occurredAtNow());
      setNext(emptyNextStep(tz));
      setAppointment(emptyAppointment());
    }
  }, [open, mode, tz]);

  const isLog = mode === 'log';
  const needsResult = isLog && RESULT_REQUIRED_TYPES.includes(type);
  const isNote = type === 'NOTA';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    // La gestión ya realizada se imputa a la fecha en que ocurrió (una visita, a su propia fecha).
    const when = isLog && !isNote ? occurredAtToDate(occurredAt) : null;
    void run(
      () =>
        mutation.mutateAsync(
          isLog
            ? {
                type,
                status: 'COMPLETADA',
                result: needsResult ? (result as InteractionResult) : null,
                nonEffectiveReason: result === 'NO_EFECTIVO' ? neReason : null,
                description: description || null,
                completedAt: when,
                visitAt: type === 'VISITA' ? when : null,
                next: leadActive && !isNote ? toNextStep(next, tz) : undefined,
              }
            : {
                type,
                status: 'PENDIENTE',
                scheduledAt: new Date(fromLocalInput(scheduledAt)),
                description: description || null,
                ...(type === 'SE_CITO' ? { appointment: toAppointmentDetails(appointment) } : {}),
              },
        ),
      isLog ? (isNote ? 'Nota guardada' : 'Gestión registrada') : 'Tarea programada',
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isLog ? 'Registrar gestión' : 'Programar tarea'}</DialogTitle>
            <DialogDescription>
              {isLog
                ? 'Una gestión ya realizada con el cliente.'
                : 'Queda como próximo paso del lead.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="int-type">Tipo</Label>
              <Select
                id="int-type"
                value={type}
                onChange={(e) => setType(e.target.value as InteractionType)}
              >
                {(isLog ? LOG_TYPES : SCHEDULE_TYPES).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </div>
            {isLog && !isNote && (
              <OccurredAtField
                id="int-occurred-at"
                value={occurredAt}
                onChange={setOccurredAt}
                label={type === 'VISITA' ? '¿Cuándo fue la visita?' : '¿Cuándo ocurrió?'}
                hint={null}
              />
            )}
            {!isLog && (
              <div className="space-y-1">
                <Label htmlFor="int-when">Fecha y hora</Label>
                <Input
                  id="int-when"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                />
                {type === 'SE_CITO' && lead && (
                  <AvailabilityHint
                    companyId={companyId}
                    projectId={lead.projectId}
                    advisorId={effectiveAppointmentAdvisor(appointment, lead.advisorId)}
                    advisorLabel={appointment.roomAdvisorId ? 'el asesor de sala' : 'el asesor'}
                    at={scheduledAt}
                  />
                )}
              </div>
            )}
          </div>
          {isLog && !isNote && (
            <p className="text-muted-foreground -mt-2 text-xs">
              Por defecto, ahora. Si fue antes, pon la fecha y hora reales (hasta 30 días atrás,
              nunca a futuro): la gestión y la visita cuentan en su propia fecha.
            </p>
          )}
          {!isLog && type === 'SE_CITO' && (
            <AppointmentFields
              value={appointment}
              onChange={setAppointment}
              projectId={lead?.projectId}
              companyId={companyId}
              idPrefix="int-appt"
            />
          )}
          {needsResult && (
            <ResultFields
              result={result}
              setResult={setResult}
              reason={neReason}
              setReason={setNeReason}
              reasons={cat.nonEffectiveReasons}
            />
          )}
          <div className="space-y-1">
            <Label htmlFor="int-desc">{isNote ? 'Nota' : 'Detalle'}</Label>
            <Textarea
              id="int-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required={isNote}
            />
          </div>
          {isLog && leadActive && !isNote && (
            <NextStepFields
              availability={
                lead
                  ? { companyId, projectId: lead.projectId, advisorId: lead.advisorId }
                  : undefined
              }
              value={next}
              onChange={setNext}
              desistReasons={cat.desistReasons}
              projectId={lead?.projectId}
              tz={tz}
            />
          )}
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || (needsResult && !result)}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar
            </Button>
            {isLog && leadActive && !isNote && (
              <NextStepSummary value={next} desistReasons={cat.desistReasons} tz={tz} />
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- reprogramar / cancelar

export function RescheduleDialog({
  task,
  leadId,
  open,
  onOpenChange,
}: {
  task: {
    id: string;
    type?: InteractionType;
    scheduledAt: string | null;
    description: string | null;
    appointment?: AppointmentInfo | null;
  } | null;
  leadId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useRescheduleInteraction(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const { data: lead } = useLead(leadId);
  const tz = useCompanyTimezone(lead?.companyId);
  const [when, setWhen] = useState(defaultNextTask(tz));
  const [appointment, setAppointment] = useState<AppointmentForm>(emptyAppointment());
  const isAppointment = task?.type === 'SE_CITO';
  const original = task?.scheduledAt ? toLocalInput(new Date(task.scheduledAt), tz) : '';
  // La nueva fecha es de ahora en adelante (la API la rechaza desde las 00:00 de hoy), pero una tarea que ya pasó
  // puede cambiar de asesor de sala, modalidad o lugar sin moverla: el mínimo aplica solo si la fecha cambia.
  const minWhen = when === original ? undefined : toLocalInput(new Date(), tz);
  useEffect(() => {
    if (open && task?.scheduledAt) setWhen(toLocalInput(new Date(task.scheduledAt), tz));
    if (open)
      setAppointment(
        task?.appointment
          ? {
              mode: task.appointment.mode,
              platform: task.appointment.platform ?? '',
              location: task.appointment.location ?? '',
              url: task.appointment.url ?? '',
              roomAdvisorId: task.appointment.roomAdvisorId ?? '',
            }
          : emptyAppointment(),
      );
  }, [open, task?.scheduledAt, task?.appointment, tz]);
  if (!task) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isAppointment ? 'max-w-lg' : 'max-w-sm'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () =>
                mutation.mutateAsync({
                  id: task.id,
                  // Si la fecha no se tocó se manda la original tal cual (con segundos): así la API sabe que no
                  // cambió, no la valida y conserva las confirmaciones de la cita.
                  scheduledAt:
                    when === original && task.scheduledAt
                      ? task.scheduledAt
                      : fromLocalInput(when, tz),
                  ...(isAppointment ? { appointment: toAppointmentDetails(appointment) } : {}),
                }),
              isAppointment ? 'Cita reprogramada' : 'Tarea reprogramada',
            );
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{isAppointment ? 'Reprogramar cita' : 'Reprogramar tarea'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="resched-when">Nueva fecha y hora</Label>
            <Input
              id="resched-when"
              type="datetime-local"
              value={when}
              min={minWhen}
              onChange={(e) => setWhen(e.target.value)}
              required
            />
            {isAppointment && lead && (
              <AvailabilityHint
                companyId={lead.companyId}
                projectId={lead.projectId}
                advisorId={effectiveAppointmentAdvisor(appointment, lead.advisorId)}
                advisorLabel={appointment.roomAdvisorId ? 'el asesor de sala' : 'el asesor'}
                at={when}
                excludeInteractionId={task.id}
              />
            )}
          </div>
          {isAppointment && (
            <AppointmentFields
              value={appointment}
              onChange={setAppointment}
              projectId={lead?.projectId}
              companyId={lead?.companyId}
              idPrefix="resched-appt"
            />
          )}
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CancelTaskDialog({
  task,
  leadId,
  companyId,
  open,
  onOpenChange,
}: {
  /** `simulated`: cita de una simulación del bot; se cancela sin exigir siguiente paso (nunca fue uno). */
  task: { id: string; simulated?: boolean } | null;
  leadId: string;
  companyId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const cat = useCatalogs(companyId).data ?? EMPTY;
  const tz = useCompanyTimezone(companyId);
  const { data: lead } = useLead(leadId);
  const mutation = useCancelInteraction(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [reason, setReason] = useState('');
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  useEffect(() => {
    if (open) {
      setReason('');
      setNext(emptyNextStep(tz));
    }
  }, [open, tz]);
  if (!task) return null;
  const simulated = task.simulated === true;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () =>
                mutation.mutateAsync({
                  id: task.id,
                  reason: reason || null,
                  next: simulated ? undefined : toNextStep(next, tz),
                }),
              simulated ? 'Simulación cancelada' : 'Tarea cancelada',
            );
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{simulated ? 'Cancelar simulación' : 'Cancelar tarea'}</DialogTitle>
            <DialogDescription>
              {simulated
                ? 'Esta cita nació de una prueba con «Simular cliente»: no cuenta y se cancela sin pedir un siguiente paso.'
                : 'El lead no puede quedar sin próximo paso.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
            <Input id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {!simulated && (
            <NextStepFields
              availability={
                lead
                  ? { companyId, projectId: lead.projectId, advisorId: lead.advisorId }
                  : undefined
              }
              value={next}
              onChange={setNext}
              desistReasons={cat.desistReasons}
              projectId={lead?.projectId}
              tz={tz}
            />
          )}
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Volver
            </Button>
            <Button type="submit" variant="destructive" disabled={mutation.isPending}>
              Cancelar tarea
            </Button>
            {!simulated && (
              <NextStepSummary value={next} desistReasons={cat.desistReasons} tz={tz} />
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- asignar / desistir / reabrir

export function AssignDialog({
  leadId,
  currentAdvisorId,
  advisors,
  open,
  onOpenChange,
}: {
  leadId: string;
  currentAdvisorId: string | null;
  advisors: AdvisorOption[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useAssignLead(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [advisorId, setAdvisorId] = useState(currentAdvisorId ?? '');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setAdvisorId(currentAdvisorId ?? '');
      setReason('');
    }
  }, [open, currentAdvisorId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () => mutation.mutateAsync({ advisorId: advisorId || null, reason: reason || null }),
              'Lead reasignado',
            );
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Asignar asesor</DialogTitle>
            <DialogDescription>Las tareas pendientes pasan al nuevo asesor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="assign-advisor">Asesor</Label>
            <Select
              id="assign-advisor"
              value={advisorId}
              onChange={(e) => setAdvisorId(e.target.value)}
            >
              <option value="">Sin asignar (bolsa)</option>
              {advisors
                .filter((a) => a.kind === 'HUMAN')
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.fullName}
                  </option>
                ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="assign-reason">Motivo (opcional)</Label>
            <Input id="assign-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              Asignar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DesistDialog({
  leadId,
  companyId,
  open,
  onOpenChange,
}: {
  leadId: string;
  companyId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const cat = useCatalogs(companyId).data ?? EMPTY;
  const mutation = useDesistLead(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () => mutation.mutateAsync({ reason, comment: comment || null }),
              'Lead desistido',
            );
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Desistir lead</DialogTitle>
            <DialogDescription>
              Se cierra el lead y se cancelan sus tareas pendientes. Podrás reabrirlo después.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="desist-reason">Motivo</Label>
            <Select
              id="desist-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Selecciona…</option>
              {cat.desistReasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="desist-comment">Comentario</Label>
            <Textarea
              id="desist-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
            />
          </div>
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={mutation.isPending || !reason}>
              Desistir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ReopenDialog({
  leadId,
  open,
  onOpenChange,
}: {
  leadId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useReopenLead(leadId);
  const { data: lead } = useLead(leadId);
  const tz = useCompanyTimezone(lead?.companyId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [comment, setComment] = useState('');
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  useEffect(() => {
    if (open) {
      setComment('');
      setNext(emptyNextStep(tz));
    }
  }, [open, tz]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // La siguiente tarea viaja completa: si es una cita, con modalidad, lugar / enlace y asesor de sala.
            void run(
              () => mutation.mutateAsync({ comment: comment || null, next: toNextTask(next, tz) }),
              'Lead reabierto',
            );
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Reabrir lead</DialogTitle>
            <DialogDescription>
              Para reabrirlo debes programar su siguiente tarea.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="reopen-comment">Motivo de la reapertura</Label>
            <Textarea
              id="reopen-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
            />
          </div>
          <NextStepFields
            availability={
              lead
                ? {
                    companyId: lead.companyId,
                    projectId: lead.projectId,
                    advisorId: lead.advisorId,
                  }
                : undefined
            }
            value={{ ...next, kind: 'TASK' }}
            onChange={(v) => setNext({ ...v, kind: 'TASK' })}
            desistReasons={[]}
            projectId={lead?.projectId}
            idPrefix="reopen"
            tz={tz}
          />
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              Reabrir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Tarea de tipo cita tal como la ven la ficha (`InteractionItem`) y Mi día (`MyDayTask`). */
export type AppointmentTaskRef = {
  id: string;
  scheduledAt: string | null;
  description: string | null;
  appointment: AppointmentInfo | null;
};

export type AttendanceOutcome = 'ASISTIO' | 'NO_ASISTIO';

/**
 * 7.22 / E1: «Confirmar asistencia» de la cita, con dos opciones excluyentes sin valor por defecto. «Asistió» confirma
 * la cita (la casilla del rol indicado o, sin rol, la que corresponde a quien confirma; con las dos, la cita queda
 * concretada, se registra la visita y el lead pasa al asesor de sala). «No asistió» cierra la cita sin visita y pide
 * reprogramar u otra tarea. Las dos exigen el siguiente paso.
 */
export function ConfirmAppointmentDialog({
  task,
  role,
  initialOutcome = null,
  leadId,
  companyId,
  open,
  onOpenChange,
}: {
  task: AppointmentTaskRef | null;
  /** Casilla que se marca; sin rol, la API deduce el de quien confirma (quien gestiona marca las dos). */
  role?: 'DIGITAL' | 'SALA';
  initialOutcome?: AttendanceOutcome | null;
  leadId: string;
  companyId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const cat = useCatalogs(companyId).data ?? EMPTY;
  const tz = useCompanyTimezone(companyId);
  const { data: lead } = useLead(leadId);
  const confirm = useConfirmAppointment(leadId);
  const complete = useCompleteInteraction(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [outcome, setOutcome] = useState<AttendanceOutcome | null>(initialOutcome);
  const [description, setDescription] = useState('');
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  useEffect(() => {
    if (open) {
      setOutcome(initialOutcome);
      setDescription('');
      setNext(
        initialOutcome === 'NO_ASISTIO'
          ? { ...emptyNextStep(tz), type: InteractionType.SE_CITO }
          : emptyNextStep(tz),
      );
    }
  }, [open, task?.id, initialOutcome, tz]);
  if (!task) return null;
  const c = task.appointment?.confirmations ?? null;
  const needsDouble = c?.needsDouble ?? false;
  // Qué casilla marca quien confirma: la indicada o, sin rol, la que le corresponde (quien gestiona marca las dos).
  const myRole: 'DIGITAL' | 'SALA' | 'BOTH' | null =
    role ??
    (c
      ? c.canConfirm.digital && c.canConfirm.room
        ? 'BOTH'
        : c.canConfirm.digital
          ? 'DIGITAL'
          : c.canConfirm.room
            ? 'SALA'
            : null
      : null);
  // Con asesor de sala distinto, la cita se concreta solo con las dos marcas: si la otra falta, esta acción
  // solo registra la propia (y el siguiente paso se pedirá a quien complete).
  const missing: 'DIGITAL' | 'SALA' | null =
    needsDouble && c && myRole === 'DIGITAL' && !c.room
      ? 'SALA'
      : needsDouble && c && myRole === 'SALA' && !c.digital
        ? 'DIGITAL'
        : null;
  const willComplete = missing === null;
  const missingLabel =
    missing === 'SALA'
      ? `del asesor de sala${task.appointment?.roomAdvisorName ? ` (${task.appointment.roomAdvisorName})` : ''}`
      : `del asesor digital${lead?.advisorName ? ` (${lead.advisorName})` : ''}`;
  const pending = confirm.isPending || complete.isPending;
  /** Aviso según lo que de verdad pasó en la API: cita concretada o solo una marca registrada. */
  const confirmedMessage = (result: unknown) => {
    const detail = result as { interactions?: Array<{ id: string; status: string }> } | null;
    const item = detail?.interactions?.find((i) => i.id === task.id);
    if (item && item.status !== 'COMPLETADA')
      return `Confirmación registrada; falta la ${missingLabel}`;
    return 'Cita concretada';
  };
  const choose = (attended: boolean) => {
    const o: AttendanceOutcome = attended ? 'ASISTIO' : 'NO_ASISTIO';
    setOutcome(o);
    // Si no vino, lo natural es volver a citarlo: se sugiere una cita como siguiente paso (se puede cambiar).
    if (o === 'NO_ASISTIO' && next.type !== InteractionType.SE_CITO)
      setNext({ ...next, type: InteractionType.SE_CITO });
    if (o === 'ASISTIO' && next.type === InteractionType.SE_CITO)
      setNext({ ...next, type: InteractionType.LLAMADA });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (outcome === 'NO_ASISTIO') {
      void run(
        () =>
          complete.mutateAsync({
            id: task.id,
            input: {
              visitAt: null,
              description: description || null,
              next: toNextStep(next, tz),
            },
          }),
        'Cita cerrada sin asistencia',
      );
      return;
    }
    void run(
      () =>
        confirm.mutateAsync({
          id: task.id,
          input: {
            ...(role ? { role } : {}),
            confirmed: true,
            description: description || null,
            // Si la otra marca falta, el siguiente paso se pedirá a quien complete la confirmación.
            ...(willComplete ? { next: toNextStep(next, tz) } : {}),
          },
        }),
      confirmedMessage,
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4" data-testid="confirm-appointment-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar asistencia</DialogTitle>
            <DialogDescription>
              Cita del {fmt.dateTimeFull(task.scheduledAt)}
              {task.description ? ` · ${task.description}` : ''}. Indica si el cliente vino y deja
              programado el siguiente paso.
            </DialogDescription>
          </DialogHeader>
          <OutcomeChoice
            value={outcome === null ? null : outcome === 'ASISTIO'}
            onChange={choose}
            yes="Asistió"
            no="No asistió"
            testId="attendance"
          />
          {outcome === 'ASISTIO' && (
            <p className="text-muted-foreground text-sm" data-testid="attendance-help">
              {!willComplete
                ? `Tu confirmación queda registrada. Falta la ${missingLabel}: cuando confirme, la cita quedará concretada, se registrará la visita a nombre del asesor de sala, el lead pasará a él y se pedirá el siguiente paso.`
                : needsDouble
                  ? 'Con la confirmación del asesor digital y la del asesor de sala la cita queda concretada, se registra la visita a nombre del asesor de sala y el lead pasa a él.'
                  : 'La cita queda concretada y se registra la visita.'}
            </p>
          )}
          {outcome === 'NO_ASISTIO' && (
            <p className="text-muted-foreground text-sm" data-testid="attendance-help">
              La cita queda cerrada sin visita (no cuenta como concretada). Reprograma la cita o
              deja otra tarea.
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor="confirm-desc">Comentario (opcional)</Label>
            <Textarea
              id="confirm-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder={outcome === 'NO_ASISTIO' ? '¿Qué pasó?' : '¿Cómo fue la visita?'}
            />
          </div>
          {(outcome !== 'ASISTIO' || willComplete) && (
            <NextStepFields
              availability={
                lead
                  ? {
                      companyId,
                      projectId: lead.projectId,
                      advisorId:
                        outcome === 'ASISTIO'
                          ? (task.appointment?.roomAdvisorId ?? lead.advisorId)
                          : lead.advisorId,
                    }
                  : undefined
              }
              value={next}
              onChange={setNext}
              desistReasons={cat.desistReasons}
              projectId={lead?.projectId}
              idPrefix="confirm-next"
              tz={tz}
            />
          )}
          {error && <p className="text-danger text-sm">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={pending || outcome === null}
              data-testid="confirm-appointment-save"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {outcome === 'NO_ASISTIO'
                ? 'Registrar inasistencia'
                : outcome === 'ASISTIO' && !willComplete
                  ? 'Registrar mi confirmación'
                  : 'Confirmar'}
            </Button>
            {(outcome !== 'ASISTIO' || willComplete) && (
              <NextStepSummary value={next} desistReasons={cat.desistReasons} tz={tz} />
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * E1: anular una visita registrada por error. Solo dentro de las 24 h desde su registro, por quien la registró o
 * quien gestiona; motivo obligatorio. La visita queda anulada (se ve en la línea de tiempo) y la cita que concretó
 * vuelve a su estado anterior.
 */
export function AnnulVisitDialog({
  visit,
  leadId,
  open,
  onOpenChange,
}: {
  visit: { id: string; visitAt: string | null } | null;
  leadId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mutation = useAnnulVisit(leadId);
  const { error, run } = useSubmit(() => onOpenChange(false));
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open, visit?.id]);
  if (!visit) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () => mutation.mutateAsync({ id: visit.id, input: { reason: reason.trim() } }),
              'Visita anulada',
            );
          }}
          className="space-y-4"
          data-testid="annul-visit-dialog"
        >
          <DialogHeader>
            <DialogTitle>Anular visita</DialogTitle>
            <DialogDescription>
              Visita del {fmt.dateTimeFull(visit.visitAt)}. La visita deja de contar en el embudo y
              la cita que concretó vuelve a su estado anterior. Solo se puede hacer dentro de las 24
              horas siguientes a registrarla y queda en el historial con tu nombre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="annul-visit-reason">Motivo</Label>
            <Textarea
              id="annul-visit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              required
              placeholder="¿Por qué se registró por error?"
            />
          </div>
          <ErrorLine error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Volver
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={mutation.isPending || !reason.trim()}
              data-testid="annul-visit-save"
            >
              Anular visita
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
